import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Build a smart range of page numbers around the current page, with
 * ellipsis for big gaps.
 *   1 2 3 4 5            (small total)
 *   1 ... 4 5 6 ... 20   (current=5, total=20)
 */
function buildPages(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const around = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => around.add(p));
  if (current >= total - 2)
    [total - 3, total - 2, total - 1].forEach((p) => around.add(p));
  const sorted = [...around]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

export default function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  onChange,
  isPaging,
}) {
  if (totalCount === 0) return null;

  const pages = buildPages(page, totalPages);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  const Btn = ({ active, disabled, onClick, ariaLabel, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={`min-w-[2.25rem] h-9 px-2 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
        active
          ? "bg-leechiuBlue text-white"
          : "text-gray-600 hover:bg-gray-100"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
      <div className="text-xs text-gray-500 cms-tabular">
        Showing <span className="font-medium text-gray-700">{start}–{end}</span>{" "}
        of <span className="font-medium text-gray-700">{totalCount}</span>
        {isPaging ? <span className="ml-2 text-gray-400">loading…</span> : null}
      </div>
      <nav
        className="flex items-center gap-1 bg-white rounded-lg ring-1 ring-gray-200 p-1"
        aria-label="Pagination"
      >
        <Btn
          onClick={() => onChange(page - 1)}
          disabled={page <= 1 || isPaging}
          ariaLabel="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </Btn>
        {pages.map((p, i) =>
          p === "…" ? (
            <span
              key={`ellipsis-${i}`}
              className="px-1 text-gray-400 select-none"
            >
              …
            </span>
          ) : (
            <Btn
              key={p}
              active={p === page}
              disabled={isPaging && p !== page}
              onClick={() => onChange(p)}
              ariaLabel={`Page ${p}`}
            >
              {p}
            </Btn>
          )
        )}
        <Btn
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages || isPaging}
          ariaLabel="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </Btn>
      </nav>
    </div>
  );
}
